import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Hero.css";

// Contenus propres au Hero : les autres traductions du site restent intactes.
const HERO_CONTENT = {
  fr: {
    sectionLabel: "Présentation des solutions UniTech Engineering",
    eyebrow: "SOLUTIONS CONNECTÉES POUR ASCENSEURS",
    proofs: ["Suivi en temps réel", "Parc multimarques", "Pensé pour les professionnels"],
    fixedTitle: "Connectez vos ascenseurs.",
    rotatingTitles: ["Anticipez les pannes.", "Intervenez plus vite."],
    description:
      "Surveillez votre parc en temps réel, anticipez les incidents et intervenez plus vite grâce à OPENlift.",
    discover: "Découvrir nos solutions",
    demo: "Demander une démo",
  },
  en: {
    sectionLabel: "Introduction to UniTech Engineering solutions",
    eyebrow: "CONNECTED SOLUTIONS FOR ELEVATORS",
    proofs: ["Real-time monitoring", "Multi-brand fleets", "Built for professionals"],
    fixedTitle: "Connect your elevators.",
    rotatingTitles: ["Anticipate issues.", "Respond faster."],
    description:
      "Monitor your elevators in real time, spot issues early and respond faster with OPENlift.",
    discover: "Explore our solutions",
    demo: "Request a demo",
  },
};

const TYPE_SPEED_MS = 72;
const ERASE_SPEED_MS = 38;
const FULL_TEXT_PAUSE_MS = 2000;
const BETWEEN_TEXT_PAUSE_MS = 320;

function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;

    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = (event) => setReducedMotion(event.matches);

    // Safari ancien et navigateurs modernes.
    if (preference.addEventListener) {
      preference.addEventListener("change", update);
      return () => preference.removeEventListener("change", update);
    }

    preference.addListener(update);
    return () => preference.removeListener(update);
  }, []);

  return reducedMotion;
}

function useTypewriter(phrases, reducedMotion) {
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    if (reducedMotion) {
      setDisplayedText(phrases[0]);
      return undefined;
    }

    let timer;
    let phraseIndex = 0;
    let characterIndex = 0;
    let deleting = false;

    setDisplayedText("");

    const tick = () => {
      const phrase = phrases[phraseIndex];
      setDisplayedText(phrase.slice(0, characterIndex));

      let wait;
      if (!deleting && characterIndex < phrase.length) {
        characterIndex += 1;
        wait = TYPE_SPEED_MS;
      } else if (!deleting) {
        deleting = true;
        wait = FULL_TEXT_PAUSE_MS;
      } else if (characterIndex > 0) {
        characterIndex -= 1;
        wait = ERASE_SPEED_MS;
      } else {
        deleting = false;
        phraseIndex = (phraseIndex + 1) % phrases.length;
        wait = BETWEEN_TEXT_PAUSE_MS;
      }

      timer = window.setTimeout(tick, wait);
    };

    timer = window.setTimeout(tick, 350);
    return () => window.clearTimeout(timer);
  }, [phrases, reducedMotion]);

  return displayedText;
}

export default function Hero() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en")
    ? "en"
    : "fr";
  const content = HERO_CONTENT[language];
  const reducedMotion = useReducedMotion();
  const typedText = useTypewriter(content.rotatingTitles, reducedMotion);
  const [videoReady, setVideoReady] = useState(false);

  const scrollToSolutions = (event) => {
    // Le lien de repli /openlift reste valable si la section n'est pas présente.
    const destination = document.querySelector(
      ".page-accueil .openlift-home, .page-accueil .openlift"
    );
    if (!destination) return;

    event.preventDefault();
    destination.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <section className="hero hero--v3" aria-label={content.sectionLabel}>
      <div className="hero-v3__media" aria-hidden="true">
        {/* Affichage de secours tant que la vidéo n'est pas disponible. 
        <div
          className={`hero-v3__fallback${videoReady && !reducedMotion ? " hero-v3__fallback--hidden" : ""}`}
        >
          <img
            src="/images/OPENliftBack.png"
            alt=""
            className="hero-v3__fallback-image"
            loading="eager"
            decoding="async"
          />
        </div>
        */}

        {/* Vidéo plein cadre : sa partie gauche blanche accueille les textes. */}
        {!reducedMotion && (
          <video
            className={`hero-v3__video${videoReady ? " hero-v3__video--ready" : ""}`}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disablePictureInPicture
            onLoadedData={() => setVideoReady(true)}
            onError={() => setVideoReady(false)}
          >
            <source src="/videos/hero-unitech.mp4" type="video/mp4" />
          </video>
        )}
      </div>

      {/* Protège la lisibilité à gauche si le cadrage vidéo change. */}
      <div className="hero-v3__readability" aria-hidden="true" />

      <div className="hero-v3__layout">
        <div className="hero-v3__content">
          <p className="hero-v3__eyebrow">{content.eyebrow}</p>
          <h1
            className="hero-v3__title"
            aria-label={`${content.fixedTitle} ${content.rotatingTitles[0]}`}
          >
            <span className="hero-v3__title-fixed" aria-hidden="true">
              {content.fixedTitle}
            </span>
            <span className="hero-v3__title-rotating" aria-hidden="true">
              <span className="hero-v3__typed-text">
                {reducedMotion ? content.rotatingTitles[0] : typedText || "\u00a0"}
              </span>
              {!reducedMotion && <span className="hero-v3__cursor" />}
            </span>
          </h1>

          <p className="hero-v3__description">{content.description}</p>

          <div className="hero-v3__actions">
            <NavLink
              to="/openlift"
              className="hero-v3__button hero-v3__button--primary"
              onClick={scrollToSolutions}
            >
              {content.discover}
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </NavLink>
            <NavLink
              to="/demo"
              className="hero-v3__button hero-v3__button--outline"
            >
              {content.demo}
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </NavLink>
          </div>

          <ul className="hero-v3__proofs" aria-label={language === "fr" ? "Atouts OPENlift" : "OPENlift benefits"}>
            {content.proofs.map((proof) => (
              <li key={proof}>{proof}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
